// Your own days that nothing accounts for — no scan, no approved leave, no
// assignment, not a rest day.
//
// The manager's board makes these gaps visible. This makes them DISAPPEAR.
// People correct their own record far more readily than they respond to
// being chased for it, and every gap closed here is one a manager never has
// to ask about — which is the whole bargain that keeps an attendance tool
// worth feeding.
//
// It deliberately shows days that are already explained-but-unsettled too
// ("you asked for this day off and nobody has answered"), because being told
// your request is still sitting there is the other half of trusting the
// system.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, CalendarPlus, CheckCircle2, HelpCircle } from 'lucide-react';
import { myPresence, type PresenceDay } from '@/services/workAssignmentService';
import { basisOf } from '../workassignments/presenceBasis';

const iso = (d: Date) => d.toISOString().slice(0, 10);

/** The window the card looks back over. Long enough to catch a forgotten day,
 *  short enough that nobody is asked to explain last quarter. */
export const LOOKBACK_DAYS = 30;

export interface Gap {
  date: string;
  /** What the resolver said — "nothing anywhere", or a record in flight. */
  label: string;
  detail?: string;
  /** True when something exists and is waiting on someone else. */
  waiting: boolean;
}

export type GapsState =
  /** We hold nothing about this person's working days in the window. Not the
   *  same as "no gaps", and the card must not imply it is. */
  | { kind: 'no_data'; gaps: Gap[] }
  /** We hold working days and none of them is a gap. The all-clear is earned. */
  | { kind: 'clear'; gaps: Gap[]; daysKnown: number }
  | { kind: 'gaps'; gaps: Gap[]; daysKnown: number };

/** Pure: what the card is allowed to claim.
 *
 * ⚠️ The distinction that matters is `no_data` vs `clear`. An employee who is
 * not on the scanner, or whose weekly upload has not landed, resolves to ZERO
 * presence rows — and reporting that as "every working day is accounted for"
 * is a confident green claim made from no evidence at all. It is the same
 * mistake as reading a blank attendance board as "nobody came to work", and
 * it is worse here because it tells someone they have nothing to fix.
 *
 * Rest days are not evidence about working days, so a window of nothing but
 * rest rows is still `no_data`. */
export const summariseGaps = (days: PresenceDay[]): GapsState => {
  const gaps = gapsFrom(days);
  const daysKnown = days.filter((d) => d.status !== 'rest').length;
  if (daysKnown === 0) return { kind: 'no_data', gaps: [] };
  if (gaps.length === 0) return { kind: 'clear', gaps: [], daysKnown };
  return { kind: 'gaps', gaps, daysKnown };
};

/** Pure: turn resolved presence into the days worth showing the employee.
 *  Only `missing` days qualify — a rest day or approved leave is not a gap,
 *  and showing them as one would train people to ignore the card. */
export const gapsFrom = (days: PresenceDay[]): Gap[] =>
  days
    .filter((d) => d.status === 'missing')
    .map((d) => {
      const basis = basisOf(d.evidence);
      return {
        date: d.date,
        label: basis.label,
        detail: basis.detail,
        waiting: Boolean(basis.actionable),
      };
    })
    .sort((a, b) => (a.date < b.date ? 1 : -1));

export const MyUnaccountedDays: React.FC<{
  onRequestLeave: (date: string) => void;
}> = ({ onRequestLeave }) => {
  const { t } = useTranslation('timeoff');
  const [state, setState] = useState<GapsState | null>(null);
  const [failed, setFailed] = useState(false);

  const load = useCallback(() => {
    const to = new Date();
    const from = new Date(Date.now() - LOOKBACK_DAYS * 86400000);
    setFailed(false);
    myPresence(iso(from), iso(to))
      .then((days) => setState(summariseGaps(days)))
      // Never silence. A card that disappears on error reads as "you are
      // fine" — the one thing we must not say when we do not know. Quiet and
      // honest, with a way to try again: this is a helpful extra on someone's
      // leave page, not the reason they came, so it does not shout.
      .catch(() => { setState(null); setFailed(true); });
  }, []);
  useEffect(load, [load]);

  const gaps = state?.gaps || [];
  const unexplained = useMemo(() => gaps.filter((g) => !g.waiting), [gaps]);

  if (failed) {
    return (
      <div className="flex flex-wrap items-center gap-2 rounded-xl border border-border bg-card p-4 text-sm text-muted-foreground">
        <HelpCircle className="h-4 w-4 shrink-0" aria-hidden />
        <span>
          {t('gaps_unknown', {
            defaultValue: 'Couldn’t check your attendance record just now, so this may be out of date.',
          })}
        </span>
        <button
          type="button"
          onClick={load}
          className="rounded-md border border-border px-2 py-0.5 text-xs font-medium text-foreground hover:bg-muted"
        >
          {t('retry', { defaultValue: 'Try again' })}
        </button>
      </div>
    );
  }
  if (state === null) return null; // first load

  // ⚠️ Nothing on file is NOT the all-clear. Saying "every day is accounted
  // for" to someone we hold no working days for tells them there is nothing
  // to fix, which may be the opposite of true.
  if (state.kind === 'no_data') {
    return (
      <div className="flex items-start gap-2 rounded-xl border border-border bg-card p-4 text-sm text-muted-foreground">
        <HelpCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
        <span>
          {t('gaps_no_data', {
            defaultValue:
              'No attendance is on file for you over the last {{days}} days, so there is nothing to check against yet. If your team uploads attendance weekly, it appears after the next upload.',
            days: LOOKBACK_DAYS,
          })}
        </span>
      </div>
    );
  }

  if (state.kind === 'clear') {
    return (
      <div className="flex items-center gap-2 rounded-xl border border-border bg-card p-4 text-sm text-success">
        <CheckCircle2 className="h-4 w-4" aria-hidden />
        {t('gaps_none', {
          defaultValue: 'All {{known}} day(s) on file in the last {{days}} days are accounted for.',
          known: state.daysKnown,
          days: LOOKBACK_DAYS,
        })}
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="mb-1 flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 text-warning" aria-hidden />
        <h3 className="text-sm font-semibold text-foreground">
          {t('gaps_title', {
            defaultValue: '{{count}} day(s) with no record',
            count: gaps.length,
          })}
        </h3>
      </div>
      <p className="mb-3 text-xs text-muted-foreground">
        {unexplained.length > 0
          ? t('gaps_help', {
              defaultValue:
                'Nothing accounts for these days — no scan, no leave, no assignment. Put it right here and nobody has to ask.',
            })
          : t('gaps_help_waiting', {
              defaultValue: 'These are already with someone else. Nothing for you to do.',
            })}
      </p>
      <ul className="divide-y divide-border">
        {gaps.slice(0, 10).map((g) => (
          <li key={g.date} className="flex flex-wrap items-center justify-between gap-2 py-2">
            <span className="min-w-0">
              <span className="text-sm tabular-nums text-foreground">{g.date}</span>
              <span className={`ml-2 text-xs ${g.waiting ? 'text-warning' : 'text-muted-foreground'}`} title={g.detail}>
                {g.label}
              </span>
            </span>
            {/* No button on a day that is already with an approver — asking
                again would create a second request, not an answer. */}
            {!g.waiting && (
              <button
                type="button"
                onClick={() => onRequestLeave(g.date)}
                className="inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1 text-xs font-medium text-foreground transition-colors hover:bg-muted"
              >
                <CalendarPlus className="h-3.5 w-3.5" aria-hidden />
                {t('gaps_request', { defaultValue: 'Request leave for this day' })}
              </button>
            )}
          </li>
        ))}
      </ul>
      {gaps.length > 10 && (
        <p className="pt-2 text-xs text-muted-foreground">
          {t('gaps_more', { defaultValue: '…and {{n}} more.', n: gaps.length - 10 })}
        </p>
      )}
    </div>
  );
};
